import { useCallback } from "react";
import { WorkerEmploymentType } from "model/Salary";
import type { StaffProfileData } from "services/api/company/staff.service";
import { changeIntroductoryToPermanent } from "../profilePanel.state.employment.logic";
import type { ModalState, TranslateFn } from "../profilePanel.state.contract";

interface UseProfilePanelChangeToPermanentModalStateParams {
  readonly companyId: number | null;
  readonly isSaving: boolean;
  readonly profile: StaffProfileData | null;
  readonly workerId: number | null;
  readonly onWorkerUpdated?: () => void;
  readonly reloadProfile: () => void;
  readonly setIsSaving: (value: boolean) => void;
  readonly setModalState: (value: ModalState) => void;
  readonly setSaveError: (value: string) => void;
  readonly translate: TranslateFn;
}

interface UseProfilePanelChangeToPermanentModalStateResult {
  readonly handleCloseChangeToPermanentModal: () => void;
  readonly handleConfirmChangeToPermanent: () => void;
  readonly handleOpenChangeToPermanentModal: () => void;
}

export const useProfilePanelChangeToPermanentModalState = (
  params: UseProfilePanelChangeToPermanentModalStateParams,
): UseProfilePanelChangeToPermanentModalStateResult => {
  const handleOpenChangeToPermanentModal = useCallback((): void => {
    if (
      params.profile?.employmentInfo?.selected?.employmentType !==
      WorkerEmploymentType.INTRODUCTORY
    ) {
      return;
    }

    params.setSaveError("");
    params.setModalState({ type: "changeToPermanent" });
  }, [params]);

  const handleCloseChangeToPermanentModal = useCallback((): void => {
    if (params.isSaving) return;
    params.setModalState({ type: "none" });
    params.setSaveError("");
  }, [params]);

  const handleConfirmChangeToPermanent = useCallback((): void => {
    if (params.isSaving) return;

    void changeIntroductoryToPermanent({
      companyId: params.companyId,
      workerId: params.workerId,
      profile: params.profile,
      translate: params.translate,
      setIsSaving: params.setIsSaving,
      setSaveError: params.setSaveError,
      closeModal: (): void => params.setModalState({ type: "none" }),
      reloadProfile: params.reloadProfile,
      onWorkerUpdated: params.onWorkerUpdated,
    });
  }, [params]);

  return {
    handleCloseChangeToPermanentModal,
    handleConfirmChangeToPermanent,
    handleOpenChangeToPermanentModal,
  };
};
